import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import { QUESTIONS } from "@/data/questions";

export default function Assessment({ onComplete, onExit }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const q = QUESTIONS[idx];
  const total = QUESTIONS.length;
  const a = answers[q.id];
  const answered = q.type === "multi" ? Array.isArray(a) && a.length > 0 : a !== undefined;
  const isLast = idx === total - 1;

  const choose = (i) => {
    if (q.type !== "multi") return setAnswers((s) => ({ ...s, [q.id]: i }));
    const cur = Array.isArray(a) ? a : [];
    const noneIdx = q.options.findIndex((o) => Object.keys(o.w).length === 0 && Object.keys(o.s).length === 0);
    let next = cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i];
    if (i === noneIdx && next.includes(i)) next = [i];
    else next = next.filter((x) => x !== noneIdx);
    if (q.max && next.length > q.max) return;
    setAnswers((s) => ({ ...s, [q.id]: next }));
  };

  const next = () => {
    if (!answered) return;
    if (isLast) return onComplete(answers);
    setIdx(idx + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => (idx === 0 ? onExit() : setIdx(idx - 1));

  return (
    <main data-testid="assessment-screen" className="px-5 sm:px-10 pb-16">
      <div className="max-w-3xl mx-auto pt-2 sm:pt-8">
        <div className="flex items-center justify-between text-xs text-[#7D5A4B] mb-3">
          <span data-testid="question-progress-text">Question {idx + 1} of {total}</span>
          <span className="tracking-[0.2em] uppercase text-[#B8892F]" data-testid="question-theme">{q.theme}</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#F6DCD4]/70 overflow-hidden" data-testid="question-progress-bar">
          <motion.div className="h-full rounded-full bg-gradient-to-r from-[#C9A24A] to-[#B76E60]" animate={{ width: `${((idx + (answered ? 1 : 0)) / total) * 100}%` }} transition={{ duration: 0.5 }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.section
            key={q.id}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}
            data-testid="question-card"
            className="glass-card rounded-3xl p-6 sm:p-10 mt-8"
          >
            <Sparkles className="w-5 h-5 text-[#C9A24A]" />
            <h1 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-[#4A2A20] mt-4 leading-snug" data-testid="question-prompt">{q.prompt}</h1>
            {q.help && <p className="text-sm text-[#9A5547] mt-3 italic">{q.help}</p>}
            {q.type === "scale" ? <ScaleOptions q={q} value={a} onPick={choose} /> : <ListOptions q={q} value={a} onPick={choose} />}
          </motion.section>
        </AnimatePresence>

        <div className="flex items-center justify-between gap-3 mt-8">
          <button data-testid="prev-question-btn" onClick={prev} className="btn-ghost-gold rounded-full px-6 py-3 text-sm inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {idx === 0 ? "Back" : "Previous"}
          </button>
          <button data-testid={isLast ? "see-results-btn" : "next-question-btn"} onClick={next} disabled={!answered} className="btn-rose rounded-full px-7 py-3 text-sm font-medium inline-flex items-center gap-2">
            {isLast ? "Reveal My Roadmap" : "Next"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </main>
  );
}

const ListOptions = ({ q, value, onPick }) => {
  const multi = q.type === "multi";
  const isOn = (i) => (multi ? (value || []).includes(i) : value === i);
  return (
    <div className="mt-8 grid gap-3" role={multi ? "group" : "radiogroup"}>
      {q.options.map((o, i) => (
        <button
          key={o.label}
          data-testid={`question-option-${i}`}
          onClick={() => onPick(i)}
          aria-pressed={isOn(i)}
          className={`option-card text-left rounded-2xl border px-5 py-4 flex items-center gap-4 ${isOn(i) ? "border-[#B76E60] bg-[#FBEFEA] shadow-[0_10px_30px_-20px_rgba(183,110,96,0.9)]" : "border-[#C9A24A]/30 bg-white/70 hover:border-[#C9A24A]/70"}`}
        >
          <span className={`grid place-items-center w-6 h-6 shrink-0 border ${multi ? "rounded-md" : "rounded-full"} ${isOn(i) ? "bg-[#B76E60] border-[#B76E60] text-white" : "border-[#C9A24A]/60"}`}>
            {isOn(i) && <Check className="w-3.5 h-3.5" />}
          </span>
          <span className="text-[15px] text-[#4A2A20] leading-snug">{o.label}</span>
        </button>
      ))}
    </div>
  );
};

const ScaleOptions = ({ q, value, onPick }) => (
  <div className="mt-8 grid grid-cols-1 sm:grid-cols-5 gap-3" role="radiogroup">
    {q.options.map((o, i) => (
      <button
        key={o.label}
        data-testid={`question-option-${i}`}
        onClick={() => onPick(i)}
        aria-pressed={value === i}
        className={`option-card rounded-2xl border px-4 py-4 sm:py-6 flex sm:flex-col items-center gap-3 sm:gap-2 text-left sm:text-center ${value === i ? "border-[#B76E60] bg-[#FBEFEA]" : "border-[#C9A24A]/30 bg-white/70 hover:border-[#C9A24A]/70"}`}
      >
        <span className={`grid place-items-center w-9 h-9 rounded-full font-serif-display text-lg shrink-0 ${value === i ? "bg-[#B76E60] text-white" : "bg-[#FAF3E8] text-[#B76E60]"}`}>{i + 1}</span>
        <span className="text-sm text-[#4A2A20] leading-tight">{o.label}</span>
      </button>
    ))}
  </div>
);
